import { useState } from 'react';

import { ChevronDown, ChevronRight, ScrollText } from 'lucide-react';

import type { AuditEntry } from '@/api/schema';
import { Page, PageHeader, Panel } from '@/components/layout/Page';
import { Chip } from '@/components/ui/Chip';
import { EmptyState, ErrorState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { Table, TableWrap, Td, Th, Tr } from '@/components/ui/Table';
import { useDateFormat } from '@/features/company/api';
import { formatDateTime } from '@/lib/dates';

import { useAuditLog } from '../api';
import styles from './AuditLogPage.module.css';

const METHODS = [
  { value: 'POST', label: 'Created' },
  { value: 'PATCH', label: 'Changed' },
  { value: 'PUT', label: 'Replaced' },
  { value: 'DELETE', label: 'Removed' },
  // Reads are not recorded in general. Documents are the exception, so this filter is
  // in practice "show me who opened a passport or a CV", which is the question the
  // exception exists to answer.
  { value: 'GET', label: 'Document opened' },
];

/** What the verb means, rather than the verb. */
const METHOD_LABEL: Record<string, string> = {
  POST: 'Created',
  PATCH: 'Changed',
  PUT: 'Replaced',
  DELETE: 'Removed',
  GET: 'Opened',
};

const METHOD_TONE: Record<
  string,
  'approved' | 'details-mailed' | 'hold' | 'rejected' | 'neutral'
> = {
  POST: 'approved',
  PATCH: 'details-mailed',
  PUT: 'hold',
  DELETE: 'rejected',
  GET: 'neutral',
};

const ENTITIES = [
  'candidates',
  'careers',
  'meetings',
  'ats',
  'screenings',
  'users',
  'roles',
  'company',
  'departments',
].map((value) => ({ value, label: value }));

/** One row, expandable to show what was sent. */
function Entry({ entry, dateFormat }: { entry: AuditEntry; dateFormat: string }) {
  const [open, setOpen] = useState(false);
  const hasPayload = entry.payload !== null && Object.keys(entry.payload).length > 0;
  const failed = entry.status_code >= 400;

  return (
    <>
      <Tr className={failed ? styles.failed : undefined}>
        <Td className={styles.when}>
          {formatDateTime(entry.at, dateFormat as Parameters<typeof formatDateTime>[1])}
        </Td>
        <Td>{entry.actor_name ?? <span className={styles.none}>Not signed in</span>}</Td>
        <Td>
          <Chip size="sm" tone={METHOD_TONE[entry.method] ?? 'neutral'}>
            {METHOD_LABEL[entry.method] ?? entry.method}
          </Chip>
        </Td>
        <Td>{entry.entity}</Td>
        <Td className={styles.path}>{entry.path}</Td>
        <Td className={styles.status}>
          {/* Failures kept rather than hidden: a refused write is often the row
              somebody is looking for. */}
          {entry.status_code}
        </Td>
        <Td>
          {hasPayload ? (
            <button
              type="button"
              className={styles.expand}
              aria-expanded={open}
              onClick={() => {
                setOpen((was) => !was);
              }}
            >
              {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <span className="visually-hidden">
                {open ? 'Hide what was sent' : 'Show what was sent'}
              </span>
            </button>
          ) : null}
        </Td>
      </Tr>
      {open && hasPayload ? (
        <Tr>
          <Td colSpan={7} className={styles.payloadCell}>
            {/* Anything secret was stripped before this was stored, not before it was
                displayed; it is not in the table at all. */}
            <pre className={styles.payload}>{JSON.stringify(entry.payload, null, 2)}</pre>
          </Td>
        </Tr>
      ) : null}
    </>
  );
}

/**
 * Settings → Audit log.
 *
 * Read only, because the API offers nothing else: the middleware is the only writer,
 * and a log that can be edited proves nothing.
 */
export function AuditLogPage() {
  const dateFormat = useDateFormat();
  const [page, setPage] = useState(1);
  const [entity, setEntity] = useState('');
  const [method, setMethod] = useState('');

  const log = useAuditLog({ page, per_page: 25, entity, method });

  return (
    <Page>
      <PageHeader title="Audit log" description="Every change, and who made it." />

      <Panel>
        <div className={styles.filters}>
          <Select
            aria-label="Area"
            value={entity}
            onChange={(event) => {
              setEntity(event.target.value);
              setPage(1);
            }}
            placeholder="Everywhere"
            options={ENTITIES}
          />
          <Select
            aria-label="Kind of change"
            value={method}
            onChange={(event) => {
              setMethod(event.target.value);
              setPage(1);
            }}
            placeholder="Any change"
            options={METHODS}
          />
          {log.data ? (
            <span className={styles.count}>
              <b>{log.data.total}</b> recorded
            </span>
          ) : null}
        </div>
      </Panel>

      {log.isPending ? (
        <div className={styles.loading}>
          <Spinner size={20} label="Loading the audit log" />
        </div>
      ) : log.isError ? (
        <ErrorState
          message="The audit log did not load."
          onRetry={() => {
            void log.refetch();
          }}
        />
      ) : log.data.items.length === 0 ? (
        <EmptyState
          icon={<ScrollText size={20} />}
          title="Nothing recorded"
          body={
            entity || method
              ? 'No changes match these filters.'
              : 'Changes appear here as soon as anybody makes one.'
          }
        />
      ) : (
        <>
          <Panel flush>
            <TableWrap>
              <Table minWidth={820}>
                <thead>
                  <Tr>
                    <Th label="When" />
                    <Th label="Who" />
                    <Th label="Change" />
                    <Th label="Area" />
                    <Th label="Path" />
                    <Th label="Result" />
                    <Th label="" />
                  </Tr>
                </thead>
                <tbody>
                  {log.data.items.map((entry) => (
                    <Entry key={entry.id} entry={entry} dateFormat={dateFormat} />
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          </Panel>

          <Pagination
            page={log.data.page}
            perPage={log.data.per_page}
            total={log.data.total}
            onChange={setPage}
            label="changes"
          />
        </>
      )}
    </Page>
  );
}
